/**
 * API client.
 *
 * Same-origin in every environment: in production the Rust binary serves this bundle
 * and `/api` from one process, and in development Vite proxies to it. There is no
 * base-URL configuration to get wrong.
 */

export type Fetch = typeof globalThis.fetch;

export interface ApiInit extends RequestInit {
	/**
	 * The `fetch` handed to a SvelteKit `load` function. Passing it through lets the
	 * framework track the request; outside a load, the global is the right default.
	 */
	fetch?: Fetch;

	/**
	 * Non-2xx statuses whose body is still the expected payload rather than an error.
	 * Rare by design — `/readyz` answering 503 is the only one so far.
	 */
	acceptStatus?: readonly number[];
}

export class ApiError extends Error {
	constructor(
		readonly status: number,
		message: string
	) {
		super(message);
		this.name = 'ApiError';
	}
}

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
	const { fetch: fetchImpl = globalThis.fetch, headers, acceptStatus, ...rest } = init;

	const response = await fetchImpl(path, {
		...rest,
		headers: {
			Accept: 'application/json',
			...(rest.body ? { 'Content-Type': 'application/json' } : {}),
			...headers
		},
		credentials: 'same-origin'
	});

	if (!response.ok && !acceptStatus?.includes(response.status)) {
		// Error bodies are JSON by contract, but a proxy or nginx can return HTML on a
		// bad day — don't let a JSON parse failure mask the real status code.
		let message = `${response.status} ${response.statusText}`;
		try {
			const body = (await response.json()) as { message?: string; error?: string };
			message = body.message ?? body.error ?? message;
		} catch {
			/* keep the status-derived message */
		}
		throw new ApiError(response.status, message);
	}

	if (response.status === 204) return undefined as T;
	return (await response.json()) as T;
}

/** `body` is serialised here so no call site has to remember `JSON.stringify`. */
function withBody(method: string) {
	return <T>(path: string, body?: unknown, init: ApiInit = {}) =>
		api<T>(path, {
			...init,
			method,
			...(body === undefined ? {} : { body: JSON.stringify(body) })
		});
}

export const post = withBody('POST');
export const patch = withBody('PATCH');
export const del = withBody('DELETE');
